// /inventory/waste · waste reason × location matrix (Phase 2b, polished).
// Helps Regina see whether expired / damaged / overproduction / display is
// the dominant pattern so she can intervene (e.g. cut tomorrow's 5寸 batch
// if expired is dominant). Date-range filter + preset shortcuts.

import { useState, useEffect, useMemo } from 'react';
import * as api from '../lib/api.ts';
import { REPORT_LOCATIONS } from '../lib/locations.ts';

// 报废分析 is a report: the two kitchens are summed into one CK column
// (backend collapses CK_RH/CK_NY → CK), matching 统计报表.
const LOCS = REPORT_LOCATIONS;
// All reasons the backend can return for a wasted event. 店员取走 / 捐献 / 其他
// are not spoilage but they ARE stock leaving the books — showing them keeps the
// report honest (it was previously blind to them).
const REASON_LABEL = {
  expired: '过期', damaged: '损坏', overproduction: '多产', display: '展示',
  donated: '捐献', staff_taken: '店员取走', other: '其他'
};
const REASONS = ['expired', 'damaged', 'overproduction', 'display', 'donated', 'staff_taken', 'other'];

function isoNDaysAgo(n) {
  return new Date(Date.now() - n * 86_400_000).toISOString().slice(0, 10);
}

const PRESETS = [
  { key: '7d',   label: '最近 7 天',  days: 6 },
  { key: '30d',  label: '最近 30 天', days: 29 },
  { key: '90d',  label: '最近 90 天', days: 89 },
  { key: '180d', label: '最近半年',   days: 179 }
];

export const InventoryWaste = () => {
  const [preset, setPreset] = useState('30d');
  const [from, setFrom] = useState(isoNDaysAgo(29));
  const [to, setTo] = useState(isoNDaysAgo(0));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  async function refresh(fromArg = from, toArg = to) {
    setLoading(true);
    setErr('');
    try {
      const d = await api.loadWasteBreakdown({ from: fromArg, to: toArg });
      setData(d);
    } catch (e) {
      setErr(e?.message || '加载失败');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { refresh(); /* eslint-disable-line */ }, []);

  function applyPreset(p) {
    const nf = isoNDaysAgo(p.days);
    const nt = isoNDaysAgo(0);
    setPreset(p.key); setFrom(nf); setTo(nt);
    refresh(nf, nt);
  }

  // Pivot rows[] into reason × location matrix
  const { matrix, grandTotal, reasonTotals, locTotals, topReason } = useMemo(() => {
    const matrix = {};
    REASONS.forEach(r => { matrix[r] = {}; LOCS.forEach(l => matrix[r][l] = 0); });
    let grandTotal = 0;
    const reasonTotals = {};
    const locTotals = {};
    REASONS.forEach(r => reasonTotals[r] = 0);
    LOCS.forEach(l => locTotals[l] = 0);
    for (const row of (data?.rows ?? [])) {
      if (matrix[row.reason] && matrix[row.reason][row.location] !== undefined) {
        matrix[row.reason][row.location] = row.qty;
        reasonTotals[row.reason] += row.qty;
        locTotals[row.location] += row.qty;
        grandTotal += row.qty;
      }
    }
    let topReason = null;
    let topVal = 0;
    for (const r of REASONS) {
      if (reasonTotals[r] > topVal) { topReason = r; topVal = reasonTotals[r]; }
    }
    return { matrix, grandTotal, reasonTotals, locTotals, topReason };
  }, [data]);

  return (
    <>
      <div className="vava-page-header">
        <div>
          <h1 className="vava-page-title">报废分析</h1>
          <div className="vava-page-meta">
            原因 × 门店报废件数 · 窗口默认最近 30 天
            {grandTotal > 0 && topReason && (
              <> · 主要原因：<strong>{REASON_LABEL[topReason]}</strong>（{reasonTotals[topReason]} 件，{((reasonTotals[topReason] / grandTotal) * 100).toFixed(0)}%）</>
            )}
          </div>
        </div>
      </div>

      <div className="inv-filter-card">
        {PRESETS.map(p => (
          <button key={p.key}
                  className={preset === p.key ? 'btn btn-primary' : 'btn btn-secondary'}
                  onClick={() => applyPreset(p)} disabled={loading}>
            {p.label}
          </button>
        ))}
        <span className="inv-divider" />
        <label className="vava-hint">从</label>
        <input type="date" value={from} max={to}
               className="vava-input inv-date-input"
               onChange={(e) => { setFrom(e.target.value); setPreset('custom'); }} />
        <label className="vava-hint">到</label>
        <input type="date" value={to} min={from} max={isoNDaysAgo(0)}
               className="vava-input inv-date-input"
               onChange={(e) => { setTo(e.target.value); setPreset('custom'); }} />
        <button className="btn btn-secondary" onClick={() => refresh()} disabled={loading}>查询</button>
      </div>

      {err && <div className="att-error inv-error" role="alert">{err}</div>}

      {loading ? (
        <div className="vava-card empty-state">加载中…</div>
      ) : grandTotal === 0 ? (
        <div className="inv-grid-wrap">
          <div className="inv-empty">
            <div className="emoji">📭</div>
            <div className="title">这个窗口内没有报废记录</div>
            <div className="sub">很好 — 或者把日期窗口拉宽看看历史。</div>
          </div>
        </div>
      ) : (
        <div className="inv-grid-wrap">
          <table className="inv-grid">
            <thead>
              <tr>
                <th>原因</th>
                {LOCS.map(l => <th key={l} className="num">{l}</th>)}
                <th className="num">合计</th>
              </tr>
            </thead>
            <tbody>
              {REASONS.map(r => (
                <tr key={r}>
                  <td className="inv-name">{REASON_LABEL[r]}</td>
                  {LOCS.map(l => (
                    <td key={l} className={`inv-cell-num readonly ${matrix[r][l] === 0 ? 'zero' : ''}`}>
                      {matrix[r][l] || '·'}
                    </td>
                  ))}
                  <td className="inv-cell-num readonly" style={{ fontWeight: 700 }}>
                    {reasonTotals[r]}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="inv-totals-row">
                <td>合计</td>
                {LOCS.map(l => (
                  <td key={l} className="inv-cell-num readonly">{locTotals[l]}</td>
                ))}
                <td className="inv-cell-num readonly" style={{ background: 'var(--vava-tan-tint)' }}>
                  {grandTotal}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      <div className="inv-footnote">
        过期 = 货架/中转过期 · 损坏 = 物理损伤 · 多产 = 当天生产过剩 · 展示 = 样品展示用
      </div>
    </>
  );
};
